import React, { useContext, useEffect, useState } from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

// My Files
import { UserContext } from "../../../context";

// Shows a list of groups which can be added to a user
export default function ManageGroupEnrolementDialog(props) {
    const { currentUserData } = useContext(UserContext);

    const [groups, setGroups] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [canMakeChanges, setCanMakeChanges] = useState(false);

    useEffect(() => {
        if (currentUserData) {
            if (
                props.hub.managers.includes(currentUserData.key) ||
                currentUserData.role === "System Digital Manager"
            ) {
                setCanMakeChanges(true);
            }
        }

        if (props.open) {
            setGroups([...props.hub.groups]);

            setSelectedGroups(
                props.hub.groups.filter((group) =>
                    group.users.some((user) => user.key === props.userKey),
                ),
            );
        }
    }, [props.open, props.hub, props.userKey, currentUserData]);

    const handleCancel = () => {
        props.onClose();
    };

    function handleDonePress() {
        props.onClose(selectedGroups.map((group) => group.key));
    }

    const handleToggle = (group) => () => {
        if (selectedGroups.includes(group)) {
            setSelectedGroups(selectedGroups.filter((g) => g !== group));
        } else {
            setSelectedGroups([...selectedGroups, group]);
        }
    };

    const visibleGroups = groups
        .filter((group) => group.name !== "All Users")
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <Dialog
            onClose={handleCancel}
            aria-labelledby="manage-groups-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="sm"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="manage-groups-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        {props.hub.name} groups
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the groups this user belongs to. These changes
                        remain unsaved until you save the user.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close group selection"
                    onClick={handleCancel}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                {!canMakeChanges && (
                    <Box
                        sx={{
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            color: "text.secondary",
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography variant="body2">
                            You are not a manager of this hub, so its group
                            memberships are read-only.
                        </Typography>
                    </Box>
                )}

                {visibleGroups.length === 0 ? (
                    <Box
                        sx={{
                            minHeight: 180,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        <GroupsOutlinedIcon
                            sx={{ fontSize: 40, mb: 1, opacity: 0.4 }}
                        />
                        <Typography
                            variant="subtitle1"
                            sx={{ color: "text.primary", fontWeight: 800 }}
                        >
                            No groups yet
                        </Typography>
                        <Typography variant="body2">
                            This hub does not have any optional groups.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}
                    >
                        {visibleGroups.map((group) => (
                            <Box
                                key={group.key}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    px: 1.75,
                                    py: 1,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <ListItemText
                                    id={`group-${group.key}`}
                                    primary={group.name}
                                    primaryTypographyProps={{
                                        sx: { fontWeight: 800 },
                                    }}
                                />
                                <Checkbox
                                    onChange={handleToggle(group)}
                                    checked={selectedGroups.includes(group)}
                                    inputProps={{
                                        "aria-labelledby": `group-${group.key}`,
                                    }}
                                    disabled={!canMakeChanges}
                                />
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Button onClick={handleCancel} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleDonePress}
                    variant="contained"
                    disableElevation
                    startIcon={<CheckIcon />}
                >
                    Apply to draft
                </Button>
            </DialogActions>
        </Dialog>
    );
}
